/**
 * Turns the `errors` array of a nestjs-zod 400 response (Zod issues) into a
 * single pt-BR sentence a user can act on, instead of the opaque
 * "Validation failed".
 */
export type ZodIssueLike = {
  code?: string;
  path?: ReadonlyArray<string | number>;
  message?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  origin?: string;
};

const FIELD_LABELS: Record<string, string> = {
  nome: 'Nome',
  descricao: 'Descrição',
  dataInicio: 'Data/hora de início',
  dataFim: 'Data/hora de término',
  local: 'Local',
  'local.endereco': 'Endereço',
  'local.cidade': 'Cidade',
  'local.uf': 'UF',
  'local.cep': 'CEP',
  valorCents: 'Valor',
  quantidade: 'Quantidade',
};

export function isZodIssueArray(value: unknown): value is ZodIssueLike[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => typeof v === 'object' && v !== null && 'path' in v)
  );
}

function fieldLabel(path: ReadonlyArray<string | number> | undefined): string | null {
  if (!path || path.length === 0) return null;
  const key = path.filter((p) => typeof p === 'string').join('.');
  return FIELD_LABELS[key] ?? FIELD_LABELS[String(path[path.length - 1])] ?? null;
}

function describe(issue: ZodIssueLike): string {
  const msg = issue.message ?? '';
  // Custom pt-BR messages from our schemas (accented or `custom` refinements).
  if (issue.code === 'custom' || /[^\x00-\x7F]/.test(msg)) return msg;
  const unit = issue.origin === 'string' ? 'caracteres' : 'item(ns)';
  switch (issue.code) {
    case 'too_small':
      return issue.origin === 'number'
        ? `deve ser no mínimo ${String(issue.minimum)}`
        : `deve ter no mínimo ${String(issue.minimum)} ${unit}`;
    case 'too_big':
      return issue.origin === 'number'
        ? `deve ser no máximo ${String(issue.maximum)}`
        : `deve ter no máximo ${String(issue.maximum)} ${unit}`;
    case 'invalid_type':
      return 'é obrigatório ou está em formato inválido';
    default:
      return 'valor inválido';
  }
}

export function formatValidationIssues(issues: ZodIssueLike[]): string {
  const parts = issues.slice(0, 3).map((issue) => {
    const label = fieldLabel(issue.path);
    const text = describe(issue);
    return label ? `${label}: ${text}` : text;
  });
  const extra = issues.length - parts.length;
  return extra > 0 ? `${parts.join('; ')} (+${extra})` : parts.join('; ');
}
